import { useUpdateInquiry } from '@inquiries/-hooks/useUpdateInquiry'
import { resolveEffectiveStartOn } from '@inquiries/-lib/effective-start-on'
import { inquiryFormSchema } from '@inquiries/-lib/inquiryValidation'
import type { InquiryResponse } from 'backend/types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  INQUIRY_ASSIGN_STATUSES,
  INQUIRY_COMPLETED_STATUS_IDS,
  type InquiryPriority,
  type InquiryRecruitmentType,
  VALIDATION_MESSAGES,
} from 'shared'
import type { SalesforceDealAno } from '@/lib/apiClients/salesforce'
import { getVisibleErrors, touchAll, validateForm } from '@/lib/form-validation'

/** 編集対象の基本情報フィールド */
export type InquiryEditFormValues = {
  overview: string
  requirements: string
  startOn: string
  endOn: string
  isStartAsap: boolean
  estimatedAmount: string
  industryName: string
  station: string
  isOnsite: boolean
  isOffsite: boolean
  isRemote: boolean
  priority: InquiryPriority
  recruitmentType: InquiryRecruitmentType | ''
  inquiryAssignStatusId: number
  inquiryCloseReason: string
  salesforceProjectId: number | null
  contactId: number | null
  primaryDivisionId: number | null
  sfLateralDivisionId: number | null
  salesMemberId: number | null
  serviceManagerMemberId: number | null
  coordinationMemberId: number | null
}

/** touched に関係なく常に表示するエラーキー（外部データ起因のバリデーション） */
const alwaysShowErrorKeys = new Set(['inquiryAssignStatusId'])

function createInitialValues(inquiry: InquiryResponse): InquiryEditFormValues {
  return {
    overview: inquiry.overview ?? '',
    requirements: inquiry.requirements,
    startOn: inquiry.start_on ?? '',
    endOn: inquiry.end_on ?? '',
    isStartAsap: inquiry.is_start_asap,
    estimatedAmount: inquiry.estimated_amount != null ? String(inquiry.estimated_amount) : '',
    industryName: inquiry.industry_name ?? '',
    station: inquiry.station ?? '',
    isOnsite: inquiry.is_onsite,
    isOffsite: inquiry.is_offsite,
    isRemote: inquiry.is_remote,
    priority: inquiry.priority as InquiryPriority,
    recruitmentType: inquiry.recruitment_type ?? '',
    inquiryAssignStatusId: inquiry.inquiry_assign_status_id,
    inquiryCloseReason: inquiry.inquiry_close_reason ?? '',
    salesforceProjectId: inquiry.salesforce_project_id,
    contactId: inquiry.contact_id,
    primaryDivisionId: inquiry.primary_division_id,
    sfLateralDivisionId: inquiry.sf_lateral_division_id,
    salesMemberId: inquiry.sales_member_id,
    serviceManagerMemberId: inquiry.service_manager_member_id,
    coordinationMemberId: inquiry.coordination_member_id,
  }
}

/**
 * ANO を替えたあとの主管部門。SF 側の部門がある ANO ならその部門にそろえる。
 * ANO を外したときと、前の部門が SF 側の部門だったときは空にする。前の ANO の部門が残ると、引合側で選んだ値のように見えるため
 */
function getPrimaryDivisionIdAfterAnoChange(
  prev: InquiryEditFormValues,
  salesforceProjectId: number | null,
  sfLateralDivisionId: number | null,
): number | null {
  if (salesforceProjectId == null) return null
  if (sfLateralDivisionId != null) return sfLateralDivisionId
  return prev.sfLateralDivisionId != null ? null : prev.primaryDivisionId
}

export function useInquiryEditForm(
  inquiry: InquiryResponse,
  hasActiveAssigns = true,
  options: { onSuccess?: () => void; onError?: (error: Error) => void } = {},
) {
  const [formValues, setFormValues] = useState<InquiryEditFormValues>(() => createInitialValues(inquiry))
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const initialValues = useMemo(() => createInitialValues(inquiry), [inquiry])

  // 別の引合が選択された or 保存成功でinquiryが更新されたらリセット
  useEffect(() => {
    setFormValues(createInitialValues(inquiry))
    setTouched({})
  }, [inquiry])

  const isDirty = useMemo(() => {
    return (Object.keys(initialValues) as (keyof InquiryEditFormValues)[]).some(
      (key) => formValues[key] !== initialValues[key],
    )
  }, [formValues, initialValues])

  // 毎レンダーでエラーを計算
  const allErrors = useMemo(() => {
    const { errors } = validateForm(inquiryFormSchema, {
      overview: formValues.overview || null,
      requirements: formValues.requirements,
      startOn: resolveEffectiveStartOn(formValues.startOn, formValues.isStartAsap),
      endOn: formValues.endOn || null,
      estimatedAmount: formValues.estimatedAmount !== '' ? Number(formValues.estimatedAmount) : null,
      industryName: formValues.industryName || null,
      station: formValues.station || null,
      priority: formValues.priority,
      recruitmentType: formValues.recruitmentType || null,
      inquiryCloseReason: formValues.inquiryCloseReason || null,
    })

    if (
      formValues.inquiryAssignStatusId === INQUIRY_ASSIGN_STATUSES.WITHDRAWN &&
      !formValues.inquiryCloseReason.trim()
    ) {
      errors.inquiryCloseReason = VALIDATION_MESSAGES.REQUIRED_SELECT
    }

    const allErrors = errors as Record<string, string>

    if (formValues.inquiryAssignStatusId === INQUIRY_ASSIGN_STATUSES.CONFIRMED && !hasActiveAssigns) {
      allErrors.inquiryAssignStatusId = VALIDATION_MESSAGES.REQUIRE_ACTIVE_ASSIGN_FOR_CONFIRMED
    }

    return allErrors
  }, [formValues, hasActiveAssigns])

  const visibleErrors = getVisibleErrors(allErrors, touched)
  for (const key of alwaysShowErrorKeys) {
    if (allErrors[key]) {
      visibleErrors[key] = allErrors[key]
    }
  }
  const formErrors = visibleErrors

  const hasErrors = Object.keys(formErrors).length > 0

  const validate = useCallback((): boolean => {
    setTouched(touchAll(inquiryFormSchema))
    return Object.keys(allErrors).length === 0
  }, [allErrors])

  const updateField = useCallback(<K extends keyof InquiryEditFormValues>(key: K, value: InquiryEditFormValues[K]) => {
    setFormValues((prev) => ({ ...prev, [key]: value }))
    setTouched((prev) => ({ ...prev, [key as string]: true }))
  }, [])

  const resetForm = useCallback(() => {
    setFormValues(createInitialValues(inquiry))
    setTouched({})
  }, [inquiry])

  // 主管部門が SF 側で決まるときは引合側の入力が反映されないため、編集不可にして送信もしない。
  // 判定はフォームで選んでいる ANO の SF 側の部門で行う（サーバーも送られた ANO で判定する）
  const isPrimaryDivisionLocked = formValues.sfLateralDivisionId != null
  const isPrimaryDivisionDisabled =
    isPrimaryDivisionLocked || INQUIRY_COMPLETED_STATUS_IDS.has(inquiry.inquiry_assign_status_id)

  /** ANO を選び直したとき（外したら null）。選んだ ANO の SF 側の部門に合わせて、関係する値をそろえる */
  const handleAnoChange = useCallback((ano: SalesforceDealAno | null) => {
    const salesforceProjectId = ano?.salesforce_project_id ?? null
    const sfLateralDivisionId = ano?.lateral_division_id ?? null
    setFormValues((prev) => ({
      ...prev,
      salesforceProjectId,
      sfLateralDivisionId,
      primaryDivisionId: getPrimaryDivisionIdAfterAnoChange(prev, salesforceProjectId, sfLateralDivisionId),
      // 営業・SMG は SF 側で決まるため、ANO を選んだら前の値を残さない
      ...(salesforceProjectId != null ? { salesMemberId: null, serviceManagerMemberId: null } : {}),
    }))
    setTouched((prev) => ({ ...prev, salesforceProjectId: true }))
  }, [])

  const { mutateAsync: save, isPending: isSaving } = useUpdateInquiry(inquiry.id, options)

  const handleSave = useCallback(() => {
    // 引合のステータスが「確定」「取り下げ」の時はステータスと取り下げ理由のみ
    if (INQUIRY_COMPLETED_STATUS_IDS.has(inquiry.inquiry_assign_status_id)) {
      return save({
        inquiryAssignStatusId: formValues.inquiryAssignStatusId,
        inquiryCloseReason: formValues.inquiryCloseReason || null,
      })
    }
    return save({
      overview: formValues.overview || null,
      requirements: formValues.requirements,
      startOn: resolveEffectiveStartOn(formValues.startOn, formValues.isStartAsap),
      endOn: formValues.endOn || null,
      estimatedAmount: formValues.estimatedAmount !== '' ? Number(formValues.estimatedAmount) : null,
      industryName: formValues.industryName || null,
      station: formValues.station || null,
      isOnsite: formValues.isOnsite,
      isOffsite: formValues.isOffsite,
      isRemote: formValues.isRemote,
      isStartAsap: formValues.isStartAsap,
      priority: formValues.priority,
      recruitmentType: formValues.recruitmentType || null,
      inquiryAssignStatusId: formValues.inquiryAssignStatusId,
      inquiryCloseReason: formValues.inquiryCloseReason || null,
      salesforceProjectId: formValues.salesforceProjectId,
      contactId: formValues.contactId,
      ...(isPrimaryDivisionLocked ? {} : { primaryDivisionId: formValues.primaryDivisionId }),
      salesMemberId: formValues.salesMemberId,
      serviceManagerMemberId: formValues.serviceManagerMemberId,
      coordinationMemberId: formValues.coordinationMemberId,
    })
  }, [formValues, save, inquiry.inquiry_assign_status_id, isPrimaryDivisionLocked])

  return {
    formValues,
    formErrors,
    hasErrors,
    isDirty,
    isSaving,
    isPrimaryDivisionLocked,
    isPrimaryDivisionDisabled,
    updateField,
    handleAnoChange,
    resetForm,
    handleSave,
    validate,
  }
}
